<template>
    <div class="pv-page">
        <!-- Header -->
        <div class="pv-header">
            <div class="pv-header__left">
                <div class="pv-header__icon"><i class="fas fa-chart-pie"></i></div>
                <div>
                    <h1 class="pv-header__title">Project Visualizations</h1>
                    <p class="pv-header__sub">Task analytics &amp; progress overview</p>
                </div>
            </div>
            <div class="pv-header__actions">
                <select class="pv-select" v-model="selectedProject" @change="getTasksByProject">
                    <option value="">All Tasks</option>
                    <option v-for="p in projects" :key="p.id" :value="p.id">
                        {{ (p.Project && p.Project.name) || p.name }}
                    </option>
                </select>
            </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="pv-loading">
            <div class="pv-spinner"></div>
            <span>Loading data...</span>
        </div>

        <!-- Empty -->
        <div v-else-if="!tasks.length" class="pv-empty">
            <div class="pv-empty__icon"><i class="fas fa-chart-bar"></i></div>
            <h3>No tasks found</h3>
            <p>Select a project or create tasks to see visualizations.</p>
        </div>

        <template v-else>
            <!-- Summary Cards -->
            <div class="pv-cards">
                <div class="pv-card" v-for="s in summaryCards" :key="s.label">
                    <div class="pv-card__icon" :style="{ background: s.bg, color: s.color }">
                        <i :class="s.icon"></i>
                    </div>
                    <div>
                        <div class="pv-card__num" :style="{ color: s.color }">{{ s.value }}</div>
                        <div class="pv-card__label">{{ s.label }}</div>
                    </div>
                </div>
            </div>

            <!-- Charts Row -->
            <div class="pv-row">
                <!-- Status Distribution (Doughnut) -->
                <div class="pv-panel pv-panel--sm">
                    <div class="pv-panel__head"><i class="fas fa-circle-notch"></i> Status Distribution</div>
                    <div class="pv-panel__body pv-panel__body--center">
                        <canvas ref="statusChart"></canvas>
                    </div>
                    <div class="pv-legend">
                        <div class="pv-legend__item" v-for="s in statusGroups" :key="s.name">
                            <span class="pv-legend__dot" :style="{ background: s.color }"></span>
                            <span class="pv-legend__name">{{ s.name }}</span>
                            <span class="pv-legend__val">{{ s.count }}</span>
                        </div>
                    </div>
                </div>

                <!-- Priority Breakdown (Bar) -->
                <div class="pv-panel pv-panel--md">
                    <div class="pv-panel__head"><i class="fas fa-flag"></i> Priority Breakdown</div>
                    <div class="pv-panel__body">
                        <canvas ref="priorityChart"></canvas>
                    </div>
                </div>
            </div>

            <!-- Second Row -->
            <div class="pv-row">
                <!-- Task Completion (Horizontal Bar) -->
                <div class="pv-panel pv-panel--full">
                    <div class="pv-panel__head"><i class="fas fa-tasks"></i> Task Progress</div>
                    <div class="pv-panel__body">
                        <canvas ref="progressChart"></canvas>
                    </div>
                </div>
            </div>

            <!-- Task Table -->
            <div class="pv-panel pv-panel--full">
                <div class="pv-panel__head"><i class="fas fa-list"></i> Task Details</div>
                <div class="pv-table-wrap">
                    <table class="pv-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Task</th>
                                <th>Assignees</th>
                                <th>Status</th>
                                <th>Priority</th>
                                <th>Start</th>
                                <th>End</th>
                                <th>Progress</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(task, i) in tasks" :key="task.id" class="pv-table__row" @click="openTask(task)">
                                <td class="pv-table__num">{{ i + 1 }}</td>
                                <td class="pv-table__name">{{ task.name }}</td>
                                <td class="pv-table__assign">{{ getAssignees(task) || '—' }}</td>
                                <td>
                                    <span class="pv-tag" :style="{ background: getStatusColor(task) + '15', color: getStatusColor(task) }">
                                        {{ getStatusName(task) }}
                                    </span>
                                </td>
                                <td>
                                    <span class="pv-tag pv-tag--pri" :style="{ background: getPriorityColor(task) + '15', color: getPriorityColor(task) }">
                                        {{ getPriorityName(task) }}
                                    </span>
                                </td>
                                <td class="pv-table__date">{{ formatDate(task.startDate) }}</td>
                                <td class="pv-table__date">{{ formatDate(task.endDate) }}</td>
                                <td>
                                    <div class="pv-prog">
                                        <div class="pv-prog__bar">
                                            <div class="pv-prog__fill" :style="{ width: getProgress(task) + '%', background: getStatusColor(task) }"></div>
                                        </div>
                                        <span class="pv-prog__val">{{ getProgress(task) }}%</span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { http } from "@/utils/http"
import Chart from "chart.js/auto"

const STATUS_COLORS = {
    completed: "#10b981", complete: "#10b981",
    "in progress": "#3b82f6",
    pending: "#f59e0b",
    blocked: "#ef4444",
    default: "#94a3b8",
}
const PRIORITY_COLORS = {
    critical: "#ef4444", high: "#f97316", medium: "#f59e0b", low: "#3b82f6", default: "#94a3b8",
}

export default {
    data() {
        return {
            loading: true,
            projects: [],
            selectedProject: "",
            tasks: [],
            charts: [],
        }
    },
    computed: {
        statusGroups() {
            const map = {}
            this.tasks.forEach(t => {
                const name = this.getStatusName(t)
                if (!map[name]) map[name] = { name, count: 0, color: this.getStatusColor(t) }
                map[name].count++
            })
            return Object.values(map)
        },
        priorityGroups() {
            const map = {}
            this.tasks.forEach(t => {
                const name = this.getPriorityName(t)
                if (!map[name]) map[name] = { name, count: 0, color: this.getPriorityColor(t) }
                map[name].count++
            })
            return Object.values(map)
        },
        summaryCards() {
            const total = this.tasks.length
            const completed = this.tasks.filter(t => this.getStatusName(t).toLowerCase() === "completed" || this.getStatusName(t).toLowerCase() === "complete").length
            const inProgress = this.tasks.filter(t => this.getStatusName(t).toLowerCase() === "in progress").length
            const overdue = this.tasks.filter(t => {
                const end = new Date(t.endDate)
                const st = this.getStatusName(t).toLowerCase()
                return end < new Date() && st !== "completed" && st !== "complete"
            }).length
            return [
                { label: "Total Tasks", value: total, icon: "fas fa-layer-group", color: "#334155", bg: "#f1f5f9" },
                { label: "Completed", value: completed, icon: "fas fa-check-circle", color: "#10b981", bg: "#ecfdf5" },
                { label: "In Progress", value: inProgress, icon: "fas fa-spinner", color: "#3b82f6", bg: "#eff6ff" },
                { label: "Overdue", value: overdue, icon: "fas fa-exclamation-triangle", color: "#ef4444", bg: "#fef2f2" },
            ]
        },
    },
    async mounted() {
        await this.getProjects()
        this.loading = false
        this.$nextTick(() => this.renderCharts())
    },
    methods: {
        async getProjects() {
            try {
                const res = await http.get("/project-tasks")
                this.projects = res.data.result || []
                this.tasks = this.projects
            } catch (e) {
                console.error("Error fetching projects:", e)
            }
        },
        async getTasksByProject() {
            if (!this.selectedProject) {
                this.tasks = this.projects
                this.$nextTick(() => this.renderCharts())
                return
            }
            try {
                const res = await http.get(`/project-tasks/${this.selectedProject}`)
                let data = res.data.result
                if (!Array.isArray(data)) data = [data]
                this.tasks = data
                this.$nextTick(() => this.renderCharts())
            } catch (e) {
                console.error("Error fetching tasks:", e)
            }
        },
        getStatusName(t) { return (t.Status && t.Status.name) || "Pending" },
        getStatusColor(t) {
            const n = this.getStatusName(t).toLowerCase()
            return STATUS_COLORS[n] || STATUS_COLORS.default
        },
        getPriorityName(t) { return (t.Priority && t.Priority.name) || "Medium" },
        getPriorityColor(t) {
            const n = this.getPriorityName(t).toLowerCase()
            return PRIORITY_COLORS[n] || PRIORITY_COLORS.default
        },
        getAssignees(t) {
            if (t.AssigneeUser && Array.isArray(t.AssigneeUser)) {
                return t.AssigneeUser.map(u => u.firstName || "").filter(Boolean).join(", ")
            }
            return ""
        },
        getProgress(t) {
            const s = this.getStatusName(t).toLowerCase()
            if (s === "completed" || s === "complete") return 100
            if (s === "in progress") {
                const start = new Date(t.startDate), end = new Date(t.endDate), now = new Date()
                const total = end - start
                if (total <= 0) return 50
                return Math.min(90, Math.max(10, Math.round(((now - start) / total) * 100)))
            }
            return 0
        },
        formatDate(d) {
            if (!d) return "—"
            const dt = new Date(d)
            return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
        },
        openTask(t) {
            if (t.slug) this.$router.push(`/project-tasks/${t.slug}`)
        },
        destroyCharts() {
            this.charts.forEach(c => c.destroy())
            this.charts = []
        },
        renderCharts() {
            this.destroyCharts()
            if (!this.tasks.length) return
            this.renderStatusChart()
            this.renderPriorityChart()
            this.renderProgressChart()
        },
        renderStatusChart() {
            const el = this.$refs.statusChart
            if (!el) return
            const groups = this.statusGroups
            this.charts.push(new Chart(el, {
                type: "doughnut",
                data: {
                    labels: groups.map(g => g.name),
                    datasets: [{
                        data: groups.map(g => g.count),
                        backgroundColor: groups.map(g => g.color),
                        borderWidth: 0, hoverOffset: 6,
                    }],
                },
                options: {
                    responsive: true, maintainAspectRatio: false,
                    cutout: "68%",
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            backgroundColor: "#1e293b", titleFont: { size: 12, weight: "600" },
                            bodyFont: { size: 12 }, padding: 10, cornerRadius: 8,
                        },
                    },
                },
            }))
        },
        renderPriorityChart() {
            const el = this.$refs.priorityChart
            if (!el) return
            const groups = this.priorityGroups
            this.charts.push(new Chart(el, {
                type: "bar",
                data: {
                    labels: groups.map(g => g.name),
                    datasets: [{
                        label: "Tasks",
                        data: groups.map(g => g.count),
                        backgroundColor: groups.map(g => g.color + "30"),
                        borderColor: groups.map(g => g.color),
                        borderWidth: 1.5, borderRadius: 6, barPercentage: 0.6,
                    }],
                },
                options: {
                    responsive: true, maintainAspectRatio: false,
                    plugins: { legend: { display: false }, tooltip: { backgroundColor: "#1e293b", cornerRadius: 8, padding: 10 } },
                    scales: {
                        y: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 11 } }, grid: { color: "#f1f5f9" } },
                        x: { ticks: { font: { size: 11, weight: "600" } }, grid: { display: false } },
                    },
                },
            }))
        },
        renderProgressChart() {
            const el = this.$refs.progressChart
            if (!el) return
            const sorted = [...this.tasks].slice(0, 20)
            this.charts.push(new Chart(el, {
                type: "bar",
                data: {
                    labels: sorted.map(t => t.name && t.name.length > 25 ? t.name.substring(0, 25) + "…" : t.name),
                    datasets: [{
                        label: "Progress",
                        data: sorted.map(t => this.getProgress(t)),
                        backgroundColor: sorted.map(t => this.getStatusColor(t) + "35"),
                        borderColor: sorted.map(t => this.getStatusColor(t)),
                        borderWidth: 1.5, borderRadius: 4, barPercentage: 0.7,
                    }],
                },
                options: {
                    indexAxis: "y", responsive: true, maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: { backgroundColor: "#1e293b", cornerRadius: 8, padding: 10,
                            callbacks: { label: (ctx) => `${ctx.parsed.x}% complete` },
                        },
                    },
                    scales: {
                        x: { beginAtZero: true, max: 100, ticks: { callback: v => v + "%", font: { size: 11 } }, grid: { color: "#f1f5f9" } },
                        y: { ticks: { font: { size: 11, weight: "500" } }, grid: { display: false } },
                    },
                },
            }))
        },
    },
    beforeDestroy() { this.destroyCharts() },
}
</script>

<style lang="scss" scoped>
@import "../../../assets/variables.scss";

.pv-page { animation: pvFade .25s ease; }
@keyframes pvFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; } }

/* ── Header ── */
.pv-header {
    display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap;
    background: white; border: 1px solid $gray100; border-radius: 10px;
    padding: 14px 20px; margin-bottom: 14px; box-shadow: $shadowSm;
}
.pv-header__left { display: flex; align-items: center; gap: 12px; }
.pv-header__icon {
    width: 38px; height: 38px; border-radius: 10px;
    background: linear-gradient(135deg, $primaryLight, darken($primaryLight, 4%));
    color: $primaryColor; display: flex; align-items: center; justify-content: center; font-size: 16px;
}
.pv-header__title { font-size: 17px; font-weight: 700; color: $gray800; margin: 0; }
.pv-header__sub { font-size: 12px; color: $gray400; margin: 2px 0 0; }
.pv-header__actions { display: flex; align-items: center; gap: 8px; }

.pv-select {
    appearance: none; padding: 7px 32px 7px 12px; border: 1px solid $gray200; border-radius: 8px;
    font-size: 13px; font-weight: 600; color: $gray700; font-family: inherit; cursor: pointer;
    background: white url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E") no-repeat right 10px center;
    transition: all $transitionFast; min-width: 180px;
    &:focus { outline: none; border-color: $primaryColor; box-shadow: 0 0 0 3px rgba($primaryColor,.1); }
}

/* ── Loading / Empty ── */
.pv-loading {
    display: flex; align-items: center; justify-content: center; gap: 10px;
    padding: 80px 16px; background: white; border: 1px solid $gray100; border-radius: 10px; box-shadow: $shadowSm;
    span { font-size: 13px; color: $gray400; }
}
.pv-spinner { width: 22px; height: 22px; border: 2.5px solid $gray200; border-top-color: $primaryColor; border-radius: 50%; animation: pvSpin .7s linear infinite; }
@keyframes pvSpin { to { transform: rotate(360deg); } }

.pv-empty {
    text-align: center; padding: 80px 16px; background: white;
    border: 1px solid $gray100; border-radius: 10px; box-shadow: $shadowSm;
    h3 { font-size: 15px; font-weight: 700; color: $gray600; margin: 0 0 4px; }
    p { font-size: 13px; color: $gray400; margin: 0; }
}
.pv-empty__icon {
    width: 52px; height: 52px; border-radius: 50%; background: $gray50; border: 1px solid $gray100;
    display: flex; align-items: center; justify-content: center; margin: 0 auto 14px;
    i { font-size: 22px; color: $gray300; }
}

/* ── Summary Cards ── */
.pv-cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 14px; }
.pv-card {
    display: flex; align-items: center; gap: 12px; padding: 14px 16px;
    background: white; border: 1px solid $gray100; border-radius: 10px; box-shadow: $shadowSm;
}
.pv-card__icon {
    width: 38px; height: 38px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center; font-size: 15px; flex-shrink: 0;
}
.pv-card__num { font-size: 20px; font-weight: 800; line-height: 1.1; }
.pv-card__label { font-size: 11.5px; color: $gray500; font-weight: 500; margin-top: 1px; }

/* ── Panels ── */
.pv-row { display: flex; gap: 14px; margin-bottom: 14px; }
.pv-panel {
    background: white; border: 1px solid $gray100; border-radius: 10px; box-shadow: $shadowSm;
    overflow: hidden; margin-bottom: 14px;
    &--sm { flex: 0 0 320px; }
    &--md { flex: 1; }
    &--full { width: 100%; }
}
.pv-panel__head {
    padding: 12px 16px; font-size: 13px; font-weight: 700; color: $gray700;
    border-bottom: 1px solid $gray100; display: flex; align-items: center; gap: 8px;
    i { color: $primaryColor; font-size: 12px; }
}
.pv-panel__body {
    padding: 16px; height: 260px; position: relative;
    &--center { display: flex; align-items: center; justify-content: center; height: 200px; }
}

/* ── Legend ── */
.pv-legend { padding: 8px 16px 12px; display: flex; flex-wrap: wrap; gap: 6px 14px; }
.pv-legend__item { display: flex; align-items: center; gap: 6px; font-size: 12px; }
.pv-legend__dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.pv-legend__name { color: $gray600; font-weight: 500; }
.pv-legend__val { font-weight: 700; color: $gray700; }

/* ── Table ── */
.pv-table-wrap { overflow-x: auto; }
.pv-table {
    width: 100%; border-collapse: collapse;
    th { text-align: left; padding: 10px 14px; font-size: 11px; font-weight: 700; color: $gray500;
        text-transform: uppercase; letter-spacing: .3px; background: $gray50; border-bottom: 1px solid $gray200; }
    td { padding: 10px 14px; font-size: 12.5px; color: $gray700; border-bottom: 1px solid $gray100; }
}
.pv-table__row {
    cursor: pointer; transition: background $transitionFast;
    &:hover { background: rgba($primaryColor, .02); }
}
.pv-table__num { font-weight: 600; color: $gray400; width: 40px; }
.pv-table__name { font-weight: 600; max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pv-table__assign { font-size: 12px; color: $gray500; max-width: 140px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pv-table__date { font-size: 12px; color: $gray500; white-space: nowrap; }

.pv-tag {
    display: inline-block; padding: 2px 8px; border-radius: 5px;
    font-size: 11px; font-weight: 600; white-space: nowrap;
}

/* ── Progress bar ── */
.pv-prog { display: flex; align-items: center; gap: 8px; min-width: 100px; }
.pv-prog__bar { flex: 1; height: 6px; background: $gray100; border-radius: 3px; overflow: hidden; }
.pv-prog__fill { height: 100%; border-radius: 3px; transition: width .3s ease; }
.pv-prog__val { font-size: 11px; font-weight: 700; color: $gray500; flex-shrink: 0; width: 32px; text-align: right; }

/* ── Responsive ── */
@media (max-width: 768px) {
    .pv-cards { grid-template-columns: repeat(2, 1fr); }
    .pv-row { flex-direction: column; }
    .pv-panel--sm { flex: none; width: 100%; }
}
</style>

#taskProgressChart {
    width: 100% !important;
    height: 100% !important;
}
</style>
